// Import reset as a separate file to avoid conflicts with other styles.
import "./styles/reset.css"
import "./styles/index.scss"

import { analyticsBootstrap, AuthProvider } from "@bcmi-labs/art-auth"
import { Snackbar } from "@bcmi-labs/art-ui/components"
import { ThemeProvider } from "@bcmi-labs/art-ui/theming"
import { noop, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { createRoot } from "react-dom/client"

import { policiesQuery } from "@/api/query/policies"
import { Devtools } from "@/components/helpers/Devtools"
import { MY_SPACE } from "@/constants/spaces"
import { env } from "@/env"
import { AuthClientInstance, queryClient, SpaceStorageInstance, themeManagerInstance } from "@/lib/services/instances"
import { initPaletteSync } from "@/lib/services/palette"
import { openExternalLink } from "@/lib/utils/browser"
import { isPWAOnChromebook } from "@/lib/utils/chromebook"
import { router } from "@/router"

/*
 * ==========================================
 * App Configuration
 * ==========================================
 */

// Hijack external links in the PWA on Chromebooks
if (isPWAOnChromebook()) {
  window.document.addEventListener(
    "click",
    event => {
      // Use closest to find the nearest 'a' element
      const linkElement = (event.target as HTMLElement).closest("a")

      if (linkElement) {
        const linkHref = linkElement.href

        // Only prevent default for external links
        if (!linkHref.startsWith(window.location.origin)) {
          event.preventDefault()
          event.stopPropagation()
          openExternalLink(linkHref)
        }
      }
    },
    true // Use capture phase to catch events before they're stopped by dropdowns
  )
}

// Subscribe to space changes and invalidate the router when the space changes.
// forcePending unmounts the matched pages right away: every space-keyed query re-keys on the live space at once, and
// a page holding a resource of the old space would 403 into the error boundary before its loader can reject it.
SpaceStorageInstance.subscribe((value, key) => {
  if (key === "space-id") {
    // Fetch the new space's permission matrix only if it has no data yet (failed or never loaded): it's never stale.
    if (value !== MY_SPACE.id) void queryClient.query(policiesQuery(value)).catch(noop)
    router.invalidate({ forcePending: true })
  }
})

// Resolve the accent palette from the network, and keep it in sync with the active space.
initPaletteSync()

// Gracefully handle preload errors on fresh deployments
window.addEventListener("vite:preloadError", event => {
  const failedPath = window.location.pathname
  const now = Date.now()

  // Check if we already tried to reload the page by storing the list of failed paths and timestamp in session storage
  const previousErrors = sessionStorage.getItem("preload-error")
  const parsedErrors = (previousErrors ? JSON.parse(previousErrors) : []) as { path: string; timestamp: number }[]
  if (previousErrors) {
    // If the path is already in the list, check if it's been more than 60 seconds since the last error
    const lastError = parsedErrors.find(error => error.path === failedPath)
    if (lastError) {
      const secondsSinceLastError = (now - lastError.timestamp) / 1000
      // Avoid reloading the page if the error happened less than 60 seconds ago
      if (secondsSinceLastError < 60) {
        console.error("Failed to preload:", event)
        return
      }
    }
  }
  sessionStorage.setItem("preload-error", JSON.stringify([...parsedErrors, { path: failedPath, timestamp: now }]))
  console.warn("Preload error detected, reloading page:", event)
  window.location.reload()
})

/*
 * ==========================================
 * App Root
 * ==========================================
 */

const root = document.getElementById("root") as HTMLElement

createRoot(root).render(
  <AuthProvider
    authClient={AuthClientInstance}
    env={{ API_URL: env.VITE_API2_URL }}
    skipRedirectCallback
    onAuthenticated={u => {
      analyticsBootstrap(u, {
        ga: {
          GTM_ID: env.VITE_GTM_ID,
          config: {
            title: "Arduino Cloud",
            environment: env.VITE_HF_CDN_ENV,
            site_area: "app_arduino"
          }
        },
        iubenda: {
          HF_CDN_URL: env.VITE_HF_CDN_URL
        },
        segment: {
          TOKEN: env.VITE_SEGMENT_TOKEN,
          SCRIPT: env.VITE_SEGMENT_SCRIPT
        },
        profitwell: import.meta.env.PROD
      })
    }}>
    <ThemeProvider themeManager={themeManagerInstance}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Snackbar.Provider />
        <Devtools router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </AuthProvider>
)
